/**
 * EIEmployees — searchable / filterable directory of profiles.
 *
 * Phase 2 layer: employment-status filter tabs (All / Active / Good Leavers /
 * Bad Leavers / Unknown) at the top, with an inline status switcher per row
 * so super-admins can re-classify on the fly.
 */

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Search, Loader2, UserCheck, UserMinus, UserX, HelpCircle } from "lucide-react";
import { API, DISC_COLOURS, formatScore, toast } from "../EmploymentIntelligence";
import RetentionFitBadge from "./RetentionFitBadge";

const STATUS_TABS = [
  { value: "",             label: "All",          Icon: null,        color: "#1a472a" },
  { value: "active",       label: "Active",       Icon: UserCheck,   color: "#10b981" },
  { value: "good_leaver",  label: "Good leavers", Icon: UserMinus,   color: "#0ea5e9" },
  { value: "bad_leaver",   label: "Bad leavers",  Icon: UserX,       color: "#e11d48" },
  { value: "unknown",      label: "Unknown",      Icon: HelpCircle,  color: "#6a707d" },
];

const STATUS_PILL = {
  active:      { label: "Active",       color: "#10b981" },
  good_leaver: { label: "Good leaver",  color: "#0ea5e9" },
  bad_leaver:  { label: "Bad leaver",   color: "#e11d48" },
  unknown:     { label: "Unknown",      color: "#6a707d" },
};

export default function EIEmployees({ onOpenProfile, initialStatus = "", title = "Employee database", blurb }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    q: "",
    employment_status: initialStatus,
    department: "",
    manager: "",
    primary_style: "",
  });
  const [opts, setOpts] = useState({ departments: [], managers: [], primary_styles: [] });
  const [counts, setCounts] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/employment-intelligence/options/filters`);
        setOpts(res.data);
      } catch { /* ignore */ }
    })();
  }, []);

  // Fetch high-level counts so tabs can show totals
  const refreshCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employment-intelligence/overview`);
      setCounts({
        total: res.data.total_profiles || 0,
        active: res.data.active_employees || 0,
        good_leaver: res.data.good_leavers || 0,
        bad_leaver: res.data.bad_leavers || 0,
        unknown: (res.data.former_employees || 0) - (res.data.good_leavers || 0) - (res.data.bad_leavers || 0),
      });
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const fetchProfiles = useCallback(async (currentFilters) => {
    const params = Object.fromEntries(Object.entries(currentFilters).filter(([, v]) => v));
    try {
      const res = await axios.get(`${API}/employment-intelligence/profiles`, { params });
      return { items: res.data.items || [], total: res.data.total || 0 };
    } catch {
      return { items: [], total: 0 };
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      setLoading(true);
      fetchProfiles(filters).then(({ items: newItems, total: newTotal }) => {
        if (cancelled) return;
        setItems(newItems);
        setTotal(newTotal);
        setLoading(false);
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [filters, fetchProfiles]);

  const handleFilter = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const setEmploymentStatus = async (profileId, value, currentName) => {
    try {
      await axios.patch(`${API}/employment-intelligence/profiles/${profileId}/employment-status`, {
        employment_status: value,
      });
      toast.success(`${currentName} → ${STATUS_PILL[value]?.label || value}`);
      // Optimistic local update
      setItems((prev) => prev.map((p) => p.id === profileId ? { ...p, employment_status: value } : p));
      refreshCounts();
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const tabCount = (val) => {
    if (val === "") return counts.total || 0;
    return counts[val] || 0;
  };

  return (
    <>
      <div className="ei-section-head">
        <div>
          <h2>{title}</h2>
          <p>{blurb || "Searchable directory across every assessment. Click a row to open the profile, or change someone's status inline."}</p>
        </div>
        <span className="ei-card-sub">
          {loading ? "Loading…" : `${total} ${total === 1 ? "profile" : "profiles"} in view`}
        </span>
      </div>

      {/* Status tabs */}
      <div className="ei-status-tabs" data-testid="ei-status-tabs">
        {STATUS_TABS.map(({ value, label, Icon, color }) => {
          const on = filters.employment_status === value;
          return (
            <button
              key={value || "all"}
              type="button"
              onClick={() => setFilters((f) => ({ ...f, employment_status: value }))}
              className={`ei-status-tab ${on ? "is-on" : ""}`}
              style={{ "--accent": color }}
              data-testid={`ei-status-tab-${value || "all"}`}
            >
              {Icon && <Icon size={13} />}
              {label}
              <span className="ei-status-tab-count">{tabCount(value)}</span>
            </button>
          );
        })}
      </div>

      <div className="ei-toolbar">
        <div className="ei-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search by name, job title or department…"
            value={filters.q}
            onChange={handleFilter("q")}
            data-testid="ei-search"
          />
        </div>
        <select className="ei-select" value={filters.department} onChange={handleFilter("department")} data-testid="ei-filter-department">
          <option value="">All departments</option>
          {opts.departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select className="ei-select" value={filters.manager} onChange={handleFilter("manager")}>
          <option value="">All managers</option>
          {opts.managers.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="ei-select" value={filters.primary_style} onChange={handleFilter("primary_style")}>
          <option value="">All DISC styles</option>
          {opts.primary_styles.map((s) => <option key={s} value={s}>{s} — {DISC_COLOURS[s].label}</option>)}
        </select>
      </div>

      <div className="ei-table-wrap">
        {loading ? (
          <div style={{ padding: 36, textAlign: "center", color: "#6a707d" }}>
            <Loader2 size={18} className="ei-spin" />
          </div>
        ) : items.length === 0 ? (
          <div style={{ padding: 36, textAlign: "center", color: "#6a707d" }}>
            No matching profiles. Adjust the filters or upload more assessments.
          </div>
        ) : (
          <table className="ei-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>DISC</th>
                <th>Status</th>
                <th>Retention&nbsp;fit</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => {
                const a = p.latest_assessment;
                const disc = a?.disc || {};
                const es = p.employment_status || "unknown";
                const esColor = STATUS_PILL[es]?.color || "#6a707d";
                return (
                  <tr key={p.id} data-testid={`ei-row-${p.id}`}>
                    <td onClick={() => onOpenProfile(p.id)} style={{ cursor: "pointer" }}>
                      <div className="ei-table-name">
                        <strong>{p.full_name}</strong>
                        <span>{p.job_title || "—"}</span>
                      </div>
                    </td>
                    <td onClick={() => onOpenProfile(p.id)} style={{ cursor: "pointer" }}>{p.department || "—"}</td>
                    <td onClick={() => onOpenProfile(p.id)} style={{ cursor: "pointer" }}>
                      {disc.primary_style ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span className={`ei-disc-chip ei-disc-chip-${disc.primary_style}`}>{disc.primary_style}</span>
                          <span className="ei-score-bars">
                            {["D", "I", "S", "C"].map((letter) => {
                              const v = disc[{ D: "dominance", I: "influence", S: "steadiness", C: "compliance" }[letter]];
                              const pct = v || 0;
                              return (
                                <span key={letter} className="ei-score-bar" title={`${letter}: ${formatScore(v)}`}>
                                  <span
                                    className="ei-score-bar-fill"
                                    style={{ background: DISC_COLOURS[letter].fill, height: `${pct}%` }}
                                  />
                                </span>
                              );
                            })}
                          </span>
                        </div>
                      ) : <span style={{ color: "#6a707d" }}>—</span>}
                    </td>
                    <td>
                      <select
                        className="ei-row-status"
                        value={es}
                        onChange={(e) => { e.stopPropagation(); setEmploymentStatus(p.id, e.target.value, p.full_name); }}
                        onClick={(e) => e.stopPropagation()}
                        style={{ "--accent": esColor }}
                        data-testid={`ei-row-status-${p.id}`}
                      >
                        <option value="active">Active</option>
                        <option value="good_leaver">Good leaver</option>
                        <option value="bad_leaver">Bad leaver</option>
                        <option value="unknown">Unknown</option>
                      </select>
                    </td>
                    <td onClick={() => onOpenProfile(p.id)} style={{ cursor: "pointer" }}>
                      <RetentionFitBadge fit={p.retention_fit} size="md" />
                    </td>
                    <td onClick={() => onOpenProfile(p.id)} style={{ cursor: "pointer" }}>
                      {a ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div className="ei-confidence-bar">
                            <div className="ei-confidence-fill" style={{ width: `${(a.ai_confidence || 0) * 100}%` }} />
                          </div>
                          <span style={{ fontSize: 12, color: "#6a707d", fontWeight: 600 }}>
                            {Math.round((a.ai_confidence || 0) * 100)}%
                          </span>
                        </div>
                      ) : <span style={{ color: "#6a707d" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
